/**
 * Cliente del endpoint /api/v1/me/notifications (Doc 21 Notif-1.c/d).
 *
 * Cubre:
 * - REST: list (paginado), unread_count, mark-as-read, mark-all-read.
 * - SSE: lo abre y mantiene `stores/notificationStreamStore.ts`.
 * - Prefs: get + patch en `/me/notification_prefs` (Notif-1.d).
 *
 * Patrón TanStack Query en componentes:
 *   const { data: count } = useQuery({
 *     queryKey: ['me', 'notifications', 'unread_count'],
 *     queryFn: () => fetchUnreadCount(accessToken),
 *     refetchInterval: 60_000,  // backup polling cada 60s
 *   })
 *
 * El SSE es la fuente principal de updates en realtime; el polling es
 * fallback por si la conexión SSE se pierde momentáneamente.
 */

import { apiFetch } from './client'

// ─── Types ──────────────────────────────────────────────────────────────

/** Event types soportados. Mismo set que el backend
 *  (app/api/v1/me.py:_SUPPORTED_EVENT_TYPES). */
export type NotificationEventType =
  | 'comment.created'
  | 'comment.replied'
  | 'comment.status_changed'
  | 'stage.activated'
  | 'artifact.rejected'
  | 'artifact.canonized'
  | 'member.added_to_project'
  | 'member.removed_from_project'
  | 'member.raci_changed'

export type NotificationChannel = 'in_app' | 'email'

export type NotificationSubjectType =
  | 'artifact'
  | 'artifact_version'
  | 'comment'
  | 'project'
  | 'project_member'
  | string

/** Shape expuesto por el backend (NotificationRead en
 *  app/api/v1/notifications.py). */
export interface NotificationItem {
  id: string
  event_type: NotificationEventType | string
  actor_user_id: string | null
  subject_type: NotificationSubjectType
  subject_id: string
  /** Payload denormalizado por el dispatch. Shape varía por event_type;
   *  el frontend tipa lo que necesita en runtime con guards. */
  payload: Record<string, unknown>
  /** ISO datetime o null si no leída. */
  read_at: string | null
  /** ISO datetime de creación. */
  created_at: string
}

export interface NotificationsListResponse {
  items: NotificationItem[]
  has_more: boolean
}

export interface UnreadCountResponse {
  count: number
}

export interface MarkAllReadResponse {
  updated: number
}

// ─── REST ───────────────────────────────────────────────────────────────

export async function fetchNotifications(
  accessToken: string,
  options: {
    activeTenantId?: string | undefined
    unread_only?: boolean
    limit?: number
    /** Cursor ISO datetime. Devuelve filas con `created_at < before`. */
    before?: string
  } = {},
): Promise<NotificationsListResponse> {
  const params = new URLSearchParams()
  if (options.unread_only) params.set('unread_only', 'true')
  if (options.limit !== undefined) params.set('limit', String(options.limit))
  if (options.before) params.set('before', options.before)
  const qs = params.toString()
  const path = `/api/v1/me/notifications${qs ? `?${qs}` : ''}`
  return apiFetch<NotificationsListResponse>(path, {
    accessToken,
    activeTenantId: options.activeTenantId,
  })
}

export async function fetchUnreadCount(
  accessToken: string,
  activeTenantId?: string | undefined,
): Promise<UnreadCountResponse> {
  return apiFetch<UnreadCountResponse>(
    '/api/v1/me/notifications/unread_count',
    { accessToken, activeTenantId },
  )
}

export async function markNotificationRead(
  accessToken: string,
  notificationId: string,
  activeTenantId?: string | undefined,
): Promise<NotificationItem> {
  return apiFetch<NotificationItem>(
    `/api/v1/me/notifications/${notificationId}`,
    {
      accessToken,
      activeTenantId,
      method: 'PATCH',
      body: { read: true },
    },
  )
}

export async function markAllNotificationsRead(
  accessToken: string,
  activeTenantId?: string | undefined,
): Promise<MarkAllReadResponse> {
  return apiFetch<MarkAllReadResponse>(
    '/api/v1/me/notifications/mark_all_read',
    {
      accessToken,
      activeTenantId,
      method: 'POST',
    },
  )
}

// ─── Notification preferences (Notif-1.d) ───────────────────────────────

export interface NotificationPrefsResponse {
  prefs: Record<string, NotificationChannel[]>
  supported_event_types: string[]
  valid_channels: string[]
}

export async function fetchNotificationPrefs(
  accessToken: string,
): Promise<NotificationPrefsResponse> {
  return apiFetch<NotificationPrefsResponse>(
    '/api/v1/me/notification_prefs',
    { accessToken },
  )
}

export async function updateNotificationPrefs(
  accessToken: string,
  prefs: Record<string, NotificationChannel[]>,
): Promise<NotificationPrefsResponse> {
  return apiFetch<NotificationPrefsResponse>(
    '/api/v1/me/notification_prefs',
    {
      accessToken,
      method: 'PATCH',
      body: { prefs },
    },
  )
}

